import React from "react";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  Info,
  X,
} from "lucide-react";
import { clearAuth } from "../../utill/auth";
import { successCodes } from "../../utill/config";
import { toUserError } from "../../utill/toUserError";
import "./notifications.scss";

// Modern toast. Same prop API as before (`show`, `onClose`, `error`), so all 43 callers
// keep working. The visual treatment is rebuilt:
//   - Severity-coded accent strip (info/success/warning/error)
//   - lucide icon by severity
//   - A thin progress bar that drains over the auto-close window — pauses on hover
//     so a fast-moving user has time to read
//   - 401 → auto-logout (unchanged behaviour; we just don't render a toast first)
//
// Accessibility: live region (`role="status"` + `aria-live="polite"`) so the toast
// is announced; the dismiss button is keyboard-reachable.
//
// Note: error code + reference id are intentionally NOT shown in the UI. They still
// flow through the API in the response envelope and end up in the server logs (keyed
// by `referenceId` MDC), which is where support actually looks.

const AUTO_DISMISS_MS = 5000;

const ICONS = {
  info: Info,
  success: CheckCircle,
  warning: AlertTriangle,
  error: AlertCircle,
};

const TITLES = {
  info: "Info",
  success: "Success",
  warning: "Warning",
  error: "Error",
};

const variantFor = (error) => {
  if (!error || typeof error.status !== "number") return "error";
  if (error.status >= 200 && error.status < 300) {
    return error.warning ? "warning" : "success";
  }
  return "error";
};

class ToastAlert extends React.Component {
  state = { phase: "entering" };
  closeTimer = null;
  enterTimer = null;
  paused = false;

  componentDidMount() {
    const sanitised = toUserError(this.props.error);

    // 401 → bail out to the login screen. This is the same behaviour as before; we
    // just don't bother rendering the toast for a single tick before reloading.
    if (sanitised && sanitised.status === 401) {
      clearAuth();
      window.location.reload();
      return;
    }

    // Cross-fade in, then start the auto-dismiss countdown. Warnings stay sticky so
    // operators don't miss them mid-flow — they require an explicit dismiss.
    this.enterTimer = setTimeout(() => this.setState({ phase: "entered" }), 20);
    if (!sanitised || !sanitised.warning) {
      this.scheduleClose();
    }
  }

  componentWillUnmount() {
    clearTimeout(this.enterTimer);
    clearTimeout(this.closeTimer);
  }

  scheduleClose = () => {
    this.closeTimer = setTimeout(this.dismiss, AUTO_DISMISS_MS);
  };

  dismiss = () => {
    clearTimeout(this.closeTimer);
    this.setState({ phase: "exiting" });
    // Wait for the exit transition before calling the caller's onClose so the toast
    // animates out smoothly instead of vanishing.
    setTimeout(() => {
      if (typeof this.props.onClose === "function") {
        this.props.onClose();
      }
    }, 200);
  };

  handlePause = () => {
    this.paused = true;
    clearTimeout(this.closeTimer);
    this.forceUpdate();
  };

  handleResume = () => {
    if (!this.paused) return;
    this.paused = false;
    this.scheduleClose();
    this.forceUpdate();
  };

  renderSuccessBody(safe) {
    const code = safe.successCode;
    const fallback = safe.warning ? "Completed with warnings." : "Action completed successfully.";
    const message = code ? successCodes[code] : fallback;
    if (safe.warning && Array.isArray(safe.logs) && safe.logs.length > 0) {
      return (
        <>
          <p>{message}</p>
          <ul>
            {safe.logs.map((entry, idx) => (
              <li key={`warn-${idx}`}>
                <strong>VIN:</strong> {entry.vin}
                <br />
                {entry.log}
              </li>
            ))}
          </ul>
        </>
      );
    }
    return <p>{message}</p>;
  }

  render() {
    if (!this.props.show) return null;
    const safe = toUserError(this.props.error);
    const variant = variantFor(safe);
    const Icon = ICONS[variant];
    const title = TITLES[variant];
    const isSuccess = variant === "success" || variant === "warning";

    return (
      <div
        className={`notification-toast notification-toast--${variant}${this.paused ? " notification-toast--paused" : ""}`}
        role="status"
        aria-live="polite"
        data-state={this.state.phase}
        onMouseEnter={this.handlePause}
        onMouseLeave={this.handleResume}
        onFocus={this.handlePause}
        onBlur={this.handleResume}
      >
        <div className="notification-toast__header">
          <Icon size={18} aria-hidden="true" />
          <p className="notification-toast__title">{title}</p>
          <button
            type="button"
            className="notification-toast__dismiss"
            onClick={this.dismiss}
            aria-label="Dismiss notification"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>
        <div className="notification-toast__body">
          {isSuccess ? this.renderSuccessBody(safe) : <p>{safe.message}</p>}
        </div>
        {!safe.warning && (
          <div className="notification-toast__progress" aria-hidden="true">
            <div
              className="notification-toast__progress-bar"
              style={{ animationDuration: `${AUTO_DISMISS_MS}ms` }}
            />
          </div>
        )}
      </div>
    );
  }
}

export default ToastAlert;
